import { useMemo, useState } from 'react';
import { PieChart } from 'lucide-react';
import { usePost } from '@/lib/useData';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ParetoPanel } from '@/components/common/ParetoPanel';
import { fmtInt, fmtEur } from '@/lib/format';

const METRICS = {
  bs_reel: { label: 'BS validés', unit: 'BS validés', fmt: (v) => fmtInt(v) },
  bs_rue: { label: 'BS signés', unit: 'BS signés', fmt: (v) => fmtInt(v) },
  montant: { label: 'Montant récurrent', unit: '€ mensuels', fmt: (v) => fmtEur(v, 0) },
};

export const RecruiterPareto = ({ body, onSelect }) => {
  const [metric, setMetric] = useState('bs_reel');
  const reqBody = useMemo(() => ({ ...body, metric }), [body, metric]);
  const { data, isLoading, isError } = usePost('/admin/recruiters/pareto', reqBody);
  const m = METRICS[metric];

  return (
    <div className="space-y-4" data-testid="recruiter-pareto">
      <div className="flex flex-col gap-3 rounded-2xl border bg-card p-4 card-shadow sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl accent-bg-100">
            <PieChart className="h-4.5 w-4.5 accent-text" />
          </span>
          <div className="min-w-0">
            <p className="font-display text-sm font-semibold text-foreground">Concentration 20/80 — Recruteurs</p>
            <p className="text-xs text-muted-foreground">Quel noyau de recruteurs génère l'essentiel des résultats ?</p>
          </div>
        </div>
        <div className="shrink-0">
          <span className="mb-1 block text-[11px] text-muted-foreground">Mesure</span>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="h-9 w-[200px] rounded-xl bg-card" data-testid="recruiter-pareto-metric"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(METRICS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isError ? (
        <EmptyState title="Accès restreint" body="Cette analyse est réservée aux administrateurs SHARITI." />
      ) : isLoading ? (
        <CardSkeleton height="h-96" />
      ) : (
        <ParetoPanel
          data={data}
          entityLabel="recruteurs"
          unitLabel={m.unit}
          valueFmt={m.fmt}
          subLabelFn={(x) => (x.association_names || []).join(' · ')}
          onSelect={onSelect ? (x) => onSelect(x.key) : undefined}
        />
      )}
    </div>
  );
};
